import React, { useState } from 'react'
import {
  Button,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Text,
  useToast,
  VStack,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  IconButton
} from '@chakra-ui/react'
import { TrashIcon } from '@heroicons/react/24/outline'
import { RentalApplication } from '@homevest/types/rental-applications'
import { useMutation } from '@tanstack/react-query'

import axios from 'lib/axios'
import { BaseFormField } from 'components/ChakraToolkit/FormField'
import { Prisma } from '@homevest/prisma-default'

type DataDict = {
  [userName: string]: {
    userId: string
    creditScore: number | null
    verifiedIncome: number | null
    alerts: Prisma.JsonValue | null
  }
}

const EditSaferentDetailsModal: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean
    onClose: () => void
    rentalApplication: RentalApplication
    onRefresh?: () => void | Promise<void>
  }>
> = ({ isOpen, onClose, rentalApplication, onRefresh }) => {
  const toast = useToast()
  const leadGroupUsers = rentalApplication.lead_group?.lead_group_users || []
  const userUnderwritingData = rentalApplication.user_underwriting_data || []
  const initialData: DataDict = {}
  leadGroupUsers.forEach((lgu) => {
    const userName = `${lgu.users.first_name || ''} ${lgu.users.last_name || ''}`.trim()
    const underwritingData = userUnderwritingData.find(
      (data) => data.user_id === lgu.users.id && !data.deactivated_at
    )
    initialData[userName] = {
      userId: underwritingData?.user_id || '',
      verifiedIncome: underwritingData?.verified_income
        ? Number(underwritingData.verified_income)
        : null,
      creditScore: underwritingData?.credit_score ?? null,
      alerts: underwritingData?.alerts ? JSON.parse(underwritingData.alerts as string) : null
    }
  })
  const [data, setData] = useState<DataDict>(initialData)

  const handleCreditScoreChange = (userName: string, value: string) => {
    const numValue = Number(value)
    setData((prev) => ({
      ...prev,
      [userName]: {
        ...prev[userName],
        creditScore: value === '' || isNaN(numValue) ? null : numValue
      }
    }))
  }

  const handleVerifiedIncomeChange = (userName: string, value: string) => {
    const numValue = Number(value)
    setData((prev) => ({
      ...prev,
      [userName]: {
        ...prev[userName],
        verifiedIncome: value === '' || isNaN(numValue) ? null : numValue
      }
    }))
  }

  const handleAlertChange = (
    userName: string,
    alertIndex: number,
    field: 'type' | 'value',
    newValue: string
  ) => {
    setData((prev) => {
      const alertsValue = prev[userName]?.alerts
      const currentAlerts = Array.isArray(alertsValue) ? [...alertsValue] : []
      const alertObj = currentAlerts[alertIndex]
      if (!alertObj || typeof alertObj !== 'object' || Array.isArray(alertObj) || alertObj === null)
        return prev

      const alert = alertObj as Record<string, any>
      const currentType = Object.keys(alert)[0]
      const currentValue = alert[currentType]

      if (field === 'type') {
        // Update the type - append " Alert" if not already present
        const typeWithAlert = newValue.endsWith(' Alert') ? newValue : `${newValue} Alert`
        const newAlert: Record<string, any> = {}
        newAlert[typeWithAlert] = currentValue
        currentAlerts[alertIndex] = newAlert
      } else {
        // Update the value - keep same type, update value
        const updatedAlert: Record<string, any> = {}
        updatedAlert[currentType] = newValue
        currentAlerts[alertIndex] = updatedAlert
      }

      return {
        ...prev,
        [userName]: {
          ...prev[userName],
          alerts: currentAlerts
        }
      }
    })
  }

  const handleAddAlert = (userName: string) => {
    setData((prev) => {
      const alertsValue = prev[userName]?.alerts
      const currentAlerts = Array.isArray(alertsValue) ? [...alertsValue] : []
      const newAlert: Record<string, any> = { '': '' }
      currentAlerts.push(newAlert)

      return {
        ...prev,
        [userName]: {
          ...prev[userName],
          alerts: currentAlerts
        }
      }
    })
  }

  const handleRemoveAlert = (userName: string, alertIndex: number) => {
    setData((prev) => {
      const alertsValue = prev[userName]?.alerts
      if (!Array.isArray(alertsValue)) return prev

      const currentAlerts = [...alertsValue]
      currentAlerts.splice(alertIndex, 1)

      return {
        ...prev,
        [userName]: {
          ...prev[userName],
          alerts: currentAlerts.length > 0 ? currentAlerts : null
        }
      }
    })
  }

  const { mutate: mutateData } = useMutation({
    mutationFn: async (
      payload: Array<{
        userId: string
        creditScore: number | null
        verifiedIncome: number | null
        alerts?: Prisma.JsonValue | null
      }>
    ) => {
      // Filter out alerts where both type and description are empty
      const filteredPayload = payload.map((item) => {
        if (!item.alerts || !Array.isArray(item.alerts)) {
          return item
        }
        const filteredAlerts = item.alerts.filter((alert: any) => {
          if (!alert || typeof alert !== 'object' || Array.isArray(alert)) return false
          const alertType = Object.keys(alert)[0] || ''
          const alertValue = alert[alertType] || ''
          // Remove " Alert" suffix for comparison
          const typeWithoutAlert = alertType.endsWith(' Alert') ? alertType.slice(0, -6) : alertType
          return typeWithoutAlert.trim() !== '' || String(alertValue).trim() !== ''
        })
        return {
          ...item,
          alerts: filteredAlerts.length > 0 ? filteredAlerts : null
        }
      })
      return axios.put('/admin/saferent/user_underwriting_data/credit_income', {
        creditIncomeData: filteredPayload
      })
    },
    onSuccess: async () => {
      toast({
        title: 'Credit Income Data Updated',
        status: 'success',
        duration: 5000
      })
      onClose()
      if (onRefresh) {
        await onRefresh()
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error Updating Credit Income Data',
        description: error?.message || 'An unknown error occurred',
        status: 'error',
        duration: 5000
      })
    }
  })

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Saferent Details</ModalHeader>
        <ModalBody>
          <VStack align='stretch' spacing={6}>
            {Object.values(data).some(
              (userData) => userData.creditScore === null || userData.verifiedIncome === null
            ) ? (
              <Text color='red.500' fontSize='sm'>
                Remember to <strong>pre-approve</strong> the application in the{' '}
                <strong>Underwriting Details Panel</strong> after inputting credit scores and
                verified annual income for all applicants.
              </Text>
            ) : (
              <Text color='teal.500' fontSize='sm'>
                All credit scores and gross annual incomes have been inputted. You can now{' '}
                <strong>pre-approve</strong> the application in the{' '}
                <strong>Underwriting Details Panel</strong>.
              </Text>
            )}
            {Object.keys(data).map((userName, index, userNames) => {
              const isLastUser = index === userNames.length - 1
              const isCreditScoreInitiallyFilled = initialData[userName]?.creditScore !== null
              const isVerifiedIncomeInitiallyFilled = initialData[userName]?.verifiedIncome !== null
              return (
                <React.Fragment key={userName}>
                  <VStack align='stretch' spacing={4}>
                    <Text fontSize='lg' fontWeight='bold'>
                      {userName}
                    </Text>
                    <BaseFormField<{ credit_score: number | null }>
                      field='credit_score'
                      label='Credit Score'
                      isRequired={false}
                      isDisabled={isCreditScoreInitiallyFilled}
                      errors={{ credit_score: '' }}
                    >
                      <Input
                        type='number'
                        min={0}
                        max={850}
                        value={data[userName]?.creditScore ?? ''}
                        onChange={(e) => handleCreditScoreChange(userName, e.target.value)}
                        isDisabled={isCreditScoreInitiallyFilled}
                        placeholder={
                          isCreditScoreInitiallyFilled
                            ? String(data[userName]?.creditScore)
                            : 'Enter credit score'
                        }
                      />
                    </BaseFormField>

                    <BaseFormField<{ verified_income: number | null }>
                      field='verified_income'
                      label='Verified Income'
                      isRequired={false}
                      errors={{ verified_income: '' }}
                    >
                      <InputGroup>
                        <InputLeftAddon>$</InputLeftAddon>
                        <Input
                          type='number'
                          min={0}
                          value={data[userName]?.verifiedIncome ?? ''}
                          onChange={(e) => handleVerifiedIncomeChange(userName, e.target.value)}
                          placeholder={
                            isVerifiedIncomeInitiallyFilled
                              ? `$${
                                  data[userName]?.verifiedIncome
                                    ? data[userName]?.verifiedIncome.toLocaleString('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })
                                    : ''
                                }`
                              : 'Enter verified income'
                          }
                        />
                      </InputGroup>
                    </BaseFormField>

                    <VStack align='stretch' spacing={3}>
                      <HStack justify='space-between' align='center'>
                        <Text fontSize='md' fontWeight='semibold'>
                          Alerts
                        </Text>
                        <Button size='xs' onClick={() => handleAddAlert(userName)}>
                          + Alert
                        </Button>
                      </HStack>
                      {data[userName]?.alerts &&
                      Array.isArray(data[userName]?.alerts) &&
                      data[userName].alerts.length > 0 ? (
                        data[userName].alerts.map((alert: any, alertIndex: number) => {
                          const alertType = Object.keys(alert)[0] || ''
                          const alertValue = alert[alertType] || ''
                          // Remove " Alert" suffix for display
                          const displayKey = alertType.endsWith(' Alert')
                            ? alertType.slice(0, -6)
                            : alertType
                          return (
                            <HStack key={alertIndex} spacing={3} align='flex-start'>
                              <BaseFormField<{ type: string }>
                                field='type'
                                label='Type'
                                isRequired={false}
                                errors={{ type: '' }}
                                formLabelProps={{ display: 'none' }}
                              >
                                <Input
                                  size='xs'
                                  fontSize='xs'
                                  value={displayKey}
                                  onChange={(e) =>
                                    handleAlertChange(userName, alertIndex, 'type', e.target.value)
                                  }
                                  placeholder='Alert type'
                                />
                              </BaseFormField>
                              <BaseFormField<{ value: string }>
                                field='value'
                                label='Description'
                                isRequired={false}
                                errors={{ value: '' }}
                                formLabelProps={{ display: 'none' }}
                              >
                                <Input
                                  size='xs'
                                  fontSize='xs'
                                  value={String(alertValue)}
                                  onChange={(e) =>
                                    handleAlertChange(userName, alertIndex, 'value', e.target.value)
                                  }
                                  placeholder='Alert description'
                                />
                              </BaseFormField>
                              <IconButton
                                aria-label='Delete alert'
                                icon={<TrashIcon style={{ width: '16px', height: '16px' }} />}
                                size='sm'
                                colorScheme='red'
                                variant='ghost'
                                onClick={() => handleRemoveAlert(userName, alertIndex)}
                                alignSelf='flex-start'
                                mt={1}
                              />
                            </HStack>
                          )
                        })
                      ) : (
                        <Text fontSize='sm' color='gray.500' fontStyle='italic'>
                          No alerts added yet. Click "Add Alert" to add one.
                        </Text>
                      )}
                    </VStack>
                  </VStack>
                  {!isLastUser && <Divider />}
                </React.Fragment>
              )
            })}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            size='xs'
            colorScheme='teal'
            onClick={() => {
              mutateData(Object.values(data))
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default EditSaferentDetailsModal
